import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  TrendingUp, AlertTriangle, CheckCircle2, XCircle, Lightbulb, ListChecks, Star,
} from 'lucide-react';

function shade(hex, amt = 0.9) {
  // lighten a hex toward white by amt (0..1)
  try {
    const h = hex.replace('#', '');
    const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
    let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    r = Math.round(r + (255 - r) * amt); g = Math.round(g + (255 - g) * amt); b = Math.round(b + (255 - b) * amt);
    return `rgb(${r},${g},${b})`;
  } catch { return '#f1f5f9'; }
}

export function PosterRenderer({ poster, size, logo, facilityName, audience, art }) {
  const p = poster || {};
  const accent = p.accent || '#0D9488';
  const blocks = p.blocks || [];
  const cols = size.w >= 22 ? 3 : size.w >= 15 ? 2 : 1;
  const base = size.w; // scale fonts a touch by width
  const bodyRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const iterRef = useRef(0);
  const contentKey = JSON.stringify([p.title, p.subtitle, blocks, p.qr?.url, size.w, size.h, !!logo, !!art, audience]);

  useLayoutEffect(() => { iterRef.current = 0; setZoom(1); }, [contentKey]);

  // Auto-fit: grow or shrink type so content fills the sheet without clipping
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || iterRef.current > 14) return;
    const rect = el.getBoundingClientRect();
    const sf = el.offsetHeight ? rect.height / el.offsetHeight : 1;
    const pad = (parseFloat(getComputedStyle(el).paddingTop) || 0) * sf;
    let maxBottom = rect.top;
    for (const ch of el.children) maxBottom = Math.max(maxBottom, ch.getBoundingClientRect().bottom);
    const avail = rect.height - pad * 2;
    const fill = maxBottom - rect.top - pad;
    const overflowX = el.scrollWidth > el.clientWidth + 2;
    iterRef.current += 1;
    if (overflowX || fill > avail + 2) setZoom((z) => +(z * 0.94).toFixed(3));
    else if (fill < avail * 0.9 && zoom < 1.8) setZoom((z) => +(z * Math.min(1.1, Math.sqrt(avail / Math.max(fill, 1)))).toFixed(3));
  }, [zoom, contentKey]);

  const F = (px) => Math.round(px * (base >= 18 ? 1 : 0.8) * zoom);

  const fullWidthTypes = new Set(['stat-row', 'bands', 'callout', 'takeaways']);

  const Heading = ({ children, icon: Icon }) => (
    <h3 style={{ color: accent, fontSize: F(22), fontWeight: 800, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, letterSpacing: '-0.01em' }}>
      {Icon && <Icon style={{ width: F(22), height: F(22) }} />} {children}
    </h3>
  );

  const Card = ({ children, span }) => (
    <div style={{ breakInside: 'avoid', columnSpan: span ? 'all' : undefined, background: '#ffffff', border: `1px solid ${shade(accent, 0.75)}`, borderRadius: 14, padding: F(18), marginBottom: F(16), boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
      {children}
    </div>
  );

  const renderBlock = (b, i) => {
    switch (b.type) {
      case 'stat-row':
        return (
          <Card key={i} span>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min((b.stats || []).length || 1, 4)}, 1fr)`, gap: F(14) }}>
              {(b.stats || []).map((s, j) => (
                <div key={j} style={{ textAlign: 'center', background: shade(accent, 0.92), borderRadius: 12, padding: F(14) }}>
                  <div style={{ fontSize: F(40), fontWeight: 900, color: accent, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: F(14), color: '#334155', marginTop: 6, fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </Card>
        );
      case 'bands':
        return (
          <Card key={i} span>
            {b.heading && <Heading icon={TrendingUp}>{b.heading}</Heading>}
            <div style={{ display: 'flex', gap: F(10) }}>
              {(b.items || []).map((it, j) => (
                <div key={j} style={{ flex: 1, background: it.color || accent, color: '#fff', borderRadius: 10, padding: F(14) }}>
                  <div style={{ fontSize: F(18), fontWeight: 800 }}>{it.label}</div>
                  <div style={{ fontSize: F(13), opacity: 0.95, marginTop: 4 }}>{it.text}</div>
                </div>
              ))}
            </div>
          </Card>
        );
      case 'numbered-grid':
        return (
          <Card key={i}>
            {b.heading && <Heading icon={ListChecks}>{b.heading}</Heading>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: F(8) }}>
              {(b.items || []).map((it, j) => (
                <div key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: F(26), height: F(26), borderRadius: '50%', background: accent, color: '#fff', fontWeight: 800, fontSize: F(14), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{j + 1}</div>
                  <div><div style={{ fontWeight: 700, fontSize: F(15), color: '#0f172a' }}>{it.name}</div>{it.desc && <div style={{ fontSize: F(13), color: '#475569' }}>{it.desc}</div>}</div>
                </div>
              ))}
            </div>
          </Card>
        );
      case 'interpretation':
        return (
          <Card key={i}>
            {b.heading && <Heading icon={TrendingUp}>{b.heading}</Heading>}
            <div style={{ display: 'grid', gap: F(8) }}>
              {(b.bands || []).map((it, j) => (
                <div key={j} style={{ borderLeft: `6px solid ${it.color || accent}`, background: shade(it.color || accent, 0.9), borderRadius: 8, padding: F(10) }}>
                  <div style={{ fontWeight: 800, fontSize: F(15), color: it.color || accent }}>{it.label}</div>
                  <div style={{ fontSize: F(13), color: '#334155' }}>{it.text}</div>
                </div>
              ))}
            </div>
            {b.note && <div style={{ marginTop: 10, fontSize: F(13), fontStyle: 'italic', color: '#64748b' }}>{b.note}</div>}
          </Card>
        );
      case 'steps':
        return (
          <Card key={i}>
            {b.heading && <Heading icon={CheckCircle2}>{b.heading}</Heading>}
            <div style={{ display: 'grid', gap: F(8) }}>
              {(b.items || []).map((it, j) => (
                <div key={j} style={{ display: 'flex', gap: 10 }}>
                  <div style={{ width: F(28), height: F(28), borderRadius: 8, background: shade(accent, 0.85), color: accent, fontWeight: 800, fontSize: F(14), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{j + 1}</div>
                  <div><div style={{ fontWeight: 700, fontSize: F(15), color: accent }}>{it.title}</div>{it.desc && <div style={{ fontSize: F(13), color: '#475569' }}>{it.desc}</div>}</div>
                </div>
              ))}
            </div>
          </Card>
        );
      case 'red-flags':
        return (
          <Card key={i}>
            <Heading icon={AlertTriangle}>{b.heading || 'Red Flags: Stop & Escalate'}</Heading>
            <div style={{ display: 'grid', gap: F(7) }}>
              {(b.items || []).map((it, j) => (
                <div key={j} style={{ display: 'flex', gap: 8, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: F(9) }}>
                  <AlertTriangle style={{ width: F(18), height: F(18), color: '#DC2626', flexShrink: 0, marginTop: 2 }} />
                  <div><span style={{ fontWeight: 800, fontSize: F(14), color: '#991B1B' }}>{it.title}</span>{it.desc && <span style={{ fontSize: F(13), color: '#7F1D1D' }}> — {it.desc}</span>}</div>
                </div>
              ))}
            </div>
          </Card>
        );
      case 'do-avoid':
        return (
          <Card key={i} span>
            {b.heading && <Heading>{b.heading}</Heading>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: F(14) }}>
              <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: F(12) }}>
                <div style={{ fontWeight: 800, color: '#047857', fontSize: F(16), display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}><CheckCircle2 style={{ width: F(18), height: F(18) }} /> DO</div>
                {(b.do || []).map((x, j) => <div key={j} style={{ fontSize: F(13), color: '#065F46', marginBottom: 4 }}>✓ {x}</div>)}
              </div>
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: F(12) }}>
                <div style={{ fontWeight: 800, color: '#B91C1C', fontSize: F(16), display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}><XCircle style={{ width: F(18), height: F(18) }} /> AVOID</div>
                {(b.avoid || []).map((x, j) => <div key={j} style={{ fontSize: F(13), color: '#991B1B', marginBottom: 4 }}>✕ {x}</div>)}
              </div>
            </div>
            {b.example && <div style={{ marginTop: 10, fontSize: F(13), fontStyle: 'italic', background: shade(accent, 0.92), borderRadius: 8, padding: F(10), color: '#334155' }}>Example: {b.example}</div>}
          </Card>
        );
      case 'checklist':
        return (
          <Card key={i}>
            {b.heading && <Heading icon={ListChecks}>{b.heading}</Heading>}
            {(b.items || []).map((x, j) => <div key={j} style={{ fontSize: F(14), color: '#334155', marginBottom: 5, display: 'flex', gap: 8 }}><CheckCircle2 style={{ width: F(16), height: F(16), color: accent, flexShrink: 0, marginTop: 2 }} />{x}</div>)}
          </Card>
        );
      case 'callout':
        return (
          <Card key={i} span>
            <div style={{ background: accent, color: '#fff', borderRadius: 10, padding: F(16), fontSize: F(17), fontWeight: 700, textAlign: 'center' }}>{b.text}</div>
          </Card>
        );
      case 'takeaways':
        return (
          <Card key={i} span>
            <Heading icon={Star}>{b.heading || 'Key Takeaways'}</Heading>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: F(10) }}>
              {(b.items || []).map((x, j) => <div key={j} style={{ display: 'flex', gap: 8, fontSize: F(14), color: '#0f172a', fontWeight: 600 }}><Star style={{ width: F(16), height: F(16), color: accent, flexShrink: 0, marginTop: 2 }} />{x}</div>)}
            </div>
          </Card>
        );
      case 'paragraph':
      default:
        return (
          <Card key={i}>
            {b.heading && <Heading icon={Lightbulb}>{b.heading}</Heading>}
            <div style={{ fontSize: F(14), color: '#334155', lineHeight: 1.5 }}>{b.text}</div>
          </Card>
        );
    }
  };

  return (
    <div className="poster-print" style={{ width: `${size.w}in`, height: `${size.h}in`, background: '#f8fafc', fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ background: accent, color: '#fff', padding: `${F(24)}px ${F(32)}px`, display: 'flex', alignItems: 'center', gap: F(24), minHeight: art ? F(200) : undefined }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: F(46), fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.02em' }}>{p.title || 'Poster Title'}</div>
          {p.subtitle && <div style={{ fontSize: F(20), opacity: 0.95, marginTop: 6, fontWeight: 500 }}>{p.subtitle}</div>}
        </div>
        {art && <img src={art} alt="" style={{ height: F(170), maxWidth: F(320), objectFit: 'contain', filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.25))', flexShrink: 0 }} data-testid="poster-art" />}
        {logo && <img src={logo} alt="logo" style={{ maxHeight: F(96), maxWidth: F(220), objectFit: 'contain', flexShrink: 0 }} />}
      </div>
      {audience === 'patient' && (
        <div style={{ background: shade(accent, 0.88), color: accent, textAlign: 'center', fontWeight: 700, fontSize: F(15), padding: `${F(6)}px 0`, letterSpacing: '0.08em', textTransform: 'uppercase' }}>For patients & families</div>
      )}
      {/* Body */}
      <div ref={bodyRef} style={{ flex: 1, padding: F(28), columnCount: cols, columnGap: F(18), overflow: 'hidden', position: 'relative' }}>
        {blocks.map(renderBlock)}
        {p.qr && p.qr.url && (
          <div style={{ breakInside: 'avoid', columnSpan: 'all', background: '#ffffff', border: `2px solid ${accent}`, borderRadius: 14, padding: F(18), marginBottom: F(16), display: 'flex', alignItems: 'center', gap: F(20) }}>
            <img alt="QR" src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&color=${(accent || '#0D9488').replace('#', '')}&data=${encodeURIComponent(p.qr.url)}`} style={{ width: F(130), height: F(130) }} />
            <div>
              <div style={{ fontSize: F(22), fontWeight: 800, color: accent }}>{p.qr.label || 'Scan for the full policy'}</div>
              {p.qr.caption && <div style={{ fontSize: F(14), color: '#475569', marginTop: 4 }}>{p.qr.caption}</div>}
              <div style={{ fontSize: F(12), color: '#94a3b8', marginTop: 6, wordBreak: 'break-all' }}>{p.qr.url}</div>
            </div>
          </div>
        )}
      </div>
      {/* Footer */}
      <div style={{ borderTop: `3px solid ${accent}`, padding: `${F(10)}px ${F(32)}px`, display: 'flex', justifyContent: 'space-between', fontSize: F(12), color: '#64748b' }}>
        <span style={{ fontWeight: 700, color: accent }}>{facilityName || 'NikoSafe Training'}</span>
        <span>{new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
}
